import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  doc,
  getDoc
} from 'firebase/firestore'
import { db, getEnvClaim } from '../../firebase'
import PaginatedTable from '../PaginatedTable'
import Spinner from '../Spinner'

export default function OrganizationsList({ orgId }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const [opts, setOpts] = useState({
    itemsPerPage: 10,
    sortBy: 'id',
    sortOrder: 'asc',
    persistent: true,
    uid: 'organizations-list'
  })

  const columns = [
    { name: 'ID', sortKey: 'id', render: (item) => item.id },
    { name: 'Name', sortKey: 'name', render: (item) => item.name }
  ]

  useEffect(() => {
    const fetchOrganizations = async () => {
      setLoading(true)
      try {
        const orgCollection = collection(db, 'organizations')
        let orgData = []

        // 👑 PLATFORM ADMIN — show all
        //
        // Was `orgId === '202509081156'`. That hardcoded id is not what the
        // rules check: `allow read: if member() && (platformAdmin() ||
        // boundTo(orgId))`. Only 1 of the 5 accounts in that organisation
        // actually carries isAdmin, so the other 4 took this branch and got a
        // permission-denied on an unfiltered list. The claim is the same fact
        // the rules read, so the two cannot drift.
        const isPlatformAdmin = (await getEnvClaim()).platformAdmin === true

        if (isPlatformAdmin) {
          let q = query(
            orgCollection,
            orderBy(opts.sortBy, opts.sortOrder),
            limit(opts.itemsPerPage)
          )

          if (currentPage > 0) {
            const prevDocsSnapshot = await getDocs(
              query(
                orgCollection,
                orderBy(opts.sortBy, opts.sortOrder),
                limit(currentPage * opts.itemsPerPage)
              )
            )
            const lastVisible =
              prevDocsSnapshot.docs[prevDocsSnapshot.docs.length - 1]
            q = query(
              orgCollection,
              orderBy(opts.sortBy, opts.sortOrder),
              startAfter(lastVisible),
              limit(opts.itemsPerPage)
            )
          }

          const snapshot = await getDocs(q)
          orgData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
          setTotalCount((await getDocs(orgCollection)).size)
        }
        // 🧩 NON-ADMIN ORG — fetch current + dispatch-bound orgs
        else {
          const dispatchBindingsRef = collection(
            db,
            'organizations',
            orgId,
            'dispatch-bindings'
          )
          const dispatchSnapshot = await getDocs(dispatchBindingsRef)
          const boundOrgIds = dispatchSnapshot.docs.map((doc) => doc.id)

          // Always include the current org itself
          const allOrgIds = [orgId, ...boundOrgIds]

          // Fetch orgs in parallel
          const orgDocs = await Promise.all(
            allOrgIds.map(async (id) => {
              const orgRef = doc(db, 'organizations', id)
              const orgSnap = await getDoc(orgRef)
              return orgSnap.exists()
                ? { id: orgSnap.id, ...orgSnap.data() }
                : null
            })
          )

          orgData = orgDocs.filter(Boolean)
          setTotalCount(orgData.length)
        }

        setRows(orgData)
      } catch (error) {
        console.error('Error fetching organizations:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchOrganizations()
  }, [currentPage, opts, orgId])

  const setPage = (page) => setCurrentPage(page)
  const setOptions = (newOpts) => {
    setOpts((prev) => ({ ...prev, ...newOpts }))
    setCurrentPage(0)
  }

  if (loading) return <Spinner />

  return (
    <div className='box'>
      <PaginatedTable
        className='is-fullwidth is-hoverable'
        columns={columns}
        rows={rows}
        currentPage={currentPage}
        totalCount={totalCount}
        loading={loading}
        setPage={setPage}
        opts={opts}
        setOpts={setOptions}
        onRowClick={(item) => navigate(`/organizations/${item.id}`)}
      />
    </div>
  )
}
